import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Sparkles, TrendingUp, TrendingDown, CreditCard, Calendar, Target, DollarSign, CheckCircle2, AlertTriangle, RefreshCw, Download, Share2, Mail, X } from 'lucide-react';
import { aiApi, API_BASE } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const ratingColor = (rating) => {
  const r = (rating || '').toLowerCase();
  if (!r) return 'text-stone-500';
  if (r.includes('excellent') || r.includes('good') || r.includes('ready')) return 'text-[#4b5320]';
  if (r.includes('moderate') || r.includes('fair') || r.includes('borderline')) return 'text-amber-700';
  return 'text-red-700';
};

const impactPill = (impact) => {
  const i = (impact || 'medium').toLowerCase();
  const cls = i === 'high' ? 'bg-red-100 text-red-700 border-red-300'
    : i === 'medium' ? 'bg-amber-100 text-amber-700 border-amber-300'
    : 'bg-stone-100 text-stone-600 border-stone-300';
  return <span className={`text-xs uppercase tracking-widest px-2 py-0.5 rounded-sm border ${cls}`}>{impact}</span>;
};

const Section = ({ title, icon: Icon, children, accent }) => (
  <div className="border border-stone-200/80 bg-white rounded-sm p-6">
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon className={`w-4 h-4 ${accent ? 'text-[#4b5320]' : 'text-stone-600'}`} />}
      <div className="text-xs uppercase tracking-widest text-[#4b5320] font-medium">{title}</div>
    </div>
    {children}
  </div>
);

const InsightsPanel = ({ clientId, clientEmail, clientName }) => {
  const { toast } = useToast();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [attachPdf, setAttachPdf] = useState(true);
  const [sending, setSending] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const load = async () => {
    try {
      const r = await aiApi.getInsights(clientId);
      setInsights(r.data && Object.keys(r.data).length ? r.data : null);
    } catch {
      // ignore - empty state will be shown
    } finally { setInitialLoad(false); }
  };

  useEffect(() => { load(); }, [clientId]);

  const pollJob = (jobId) => new Promise((resolve, reject) => {
    let attempts = 0;
    const maxAttempts = 100; // 100 * 3s = 5 minutes
    const tick = async () => {
      attempts += 1;
      try {
        const r = await aiApi.getInsightsJob(clientId, jobId);
        setElapsed(r.data.elapsedSeconds || 0);
        if (r.data.status === 'complete') {
          resolve(r.data.result);
        } else if (r.data.status === 'failed') {
          reject(new Error(r.data.error || 'Generation failed'));
        } else if (attempts >= maxAttempts) {
          reject(new Error('Timed out after 5 minutes. Please try again.'));
        } else {
          setTimeout(tick, 3000);
        }
      } catch (e) {
        if (attempts >= maxAttempts) reject(e);
        else setTimeout(tick, 3000);
      }
    };
    tick();
  });

  const generate = async () => {
    setLoading(true);
    setElapsed(0);
    try {
      toast({ title: 'Analyzing credit reports...', description: 'This can take 30-90 seconds for multi-bureau reports. You can stay on this page.' });
      const start = await aiApi.insights(clientId);
      const jobId = start.data.jobId;
      if (!jobId) throw new Error('No job id returned');
      const result = await pollJob(jobId);
      setInsights(result);
      toast({ title: 'Insights ready', description: 'Comprehensive credit health analysis generated.' });
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || e.message || 'Try again.' });
    } finally {
      setLoading(false);
      setElapsed(0);
    }
  };

  const downloadPdf = () => {
    window.open(aiApi.insightsPdfUrl(clientId), '_blank');
  };

  const copyShareLink = async () => {
    try {
      const r = await aiApi.createInsightsShare(clientId);
      const fullUrl = `${API_BASE}${r.data.url.replace(/^\/api/, '')}`;
      try {
        await navigator.clipboard.writeText(fullUrl);
        toast({ title: 'Share link copied', description: 'Valid for 30 days. Anyone with the link can view the report.' });
      } catch {
        window.prompt('Copy the share link:', fullUrl);
      }
    } catch (e) {
      toast({ title: 'Could not create share link', description: e.response?.data?.detail || 'Try again.' });
    }
  };

  const revokeShareLink = async () => {
    if (!window.confirm('Revoke ALL active share links for this client? Anyone with an existing link will lose access immediately.')) return;
    try {
      const r = await aiApi.revokeInsightsShare(clientId);
      toast({ title: 'Share links revoked', description: `${r.data.revoked} link(s) deactivated.` });
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || 'Could not revoke.' });
    }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const r = await aiApi.emailInsights(clientId, { message: emailMessage, attachPdf });
      toast({ title: 'Email sent', description: `Delivered to ${r.data.to}.` });
      setEmailOpen(false);
      setEmailMessage('');
    } catch (e) {
      toast({ title: 'Email failed', description: e.response?.data?.detail || 'Could not send.' });
    } finally {
      setSending(false);
    }
  };

  if (initialLoad) return <div className="p-8 text-center text-stone-500">Loading insights...</div>;

  if (!insights) {
    return (
      <div className="border border-stone-200/80 bg-white rounded-sm p-12 text-center">
        <Sparkles className="w-10 h-10 text-[#4b5320] mx-auto mb-4" strokeWidth={1.5} />
        <h3 className="font-display text-2xl text-stone-900 mb-2">Generate Credit Health Insights</h3>
        <p className="text-sm text-stone-600 max-w-xl mx-auto mb-6">
          AI-powered comprehensive analysis: score summary, reasons for low scores, dispute opportunities,
          utilization & payment history, personalized improvement plan, estimated score impact, and funding readiness.
        </p>
        <Button onClick={generate} disabled={loading} data-testid="insights-generate-btn" className="bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm">
          {loading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing... {elapsed}s</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate Insights</>}
        </Button>
        <div className="text-xs text-stone-500 mt-3">Requires uploaded credit report PDFs</div>
      </div>
    );
  }

  const ss = insights.scoreSummary || {};
  const ua = insights.utilizationAnalysis || {};
  const ph = insights.paymentHistoryAnalysis || {};
  const fr = insights.fundingReadiness || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-end gap-2">
        <Button onClick={() => setEmailOpen(true)} disabled={!clientEmail} data-testid="insights-email-client" className="bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm disabled:opacity-40" title={clientEmail ? `Email to ${clientEmail}` : 'Client has no email on file'}>
          <Mail className="w-4 h-4 mr-2" /> Email to Client
        </Button>
        <Button onClick={downloadPdf} variant="outline" data-testid="insights-download-pdf" className="border-stone-300 bg-white text-stone-700 hover:bg-stone-100 rounded-sm">
          <Download className="w-4 h-4 mr-2" /> Download PDF
        </Button>
        <Button onClick={copyShareLink} variant="outline" data-testid="insights-share-link" className="border-stone-300 bg-white text-stone-700 hover:bg-stone-100 rounded-sm">
          <Share2 className="w-4 h-4 mr-2" /> Copy Share Link
        </Button>
        <Button onClick={revokeShareLink} variant="outline" data-testid="insights-revoke-share" className="border-stone-300 bg-white text-stone-700 hover:bg-stone-100 rounded-sm" title="Revoke all active share links">
          <X className="w-4 h-4 mr-2" /> Revoke Links
        </Button>
        <Button onClick={generate} disabled={loading} variant="outline" data-testid="insights-regenerate" className="border-stone-300 bg-white text-stone-700 hover:bg-stone-100 rounded-sm">
          {loading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Regenerating... {elapsed}s</> : <><RefreshCw className="w-4 h-4 mr-2" /> Regenerate</>}
        </Button>
      </div>

      {/* Score Summary */}
      <Section title="Score Summary" icon={Target} accent>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {['equifax', 'experian', 'transunion'].map(b => (
            <div key={b} className="border-l-2 border-stone-300 pl-3">
              <div className="text-xs uppercase tracking-widest text-stone-500">{b}</div>
              <div className="font-display text-3xl text-stone-900">{ss[b] ?? '—'}</div>
            </div>
          ))}
          <div className="border-l-2 border-[#4b5320] pl-3">
            <div className="text-xs uppercase tracking-widest text-[#4b5320]">Average</div>
            <div className="font-display text-3xl text-[#4b5320]">{ss.average ?? '—'}</div>
          </div>
          <div className="border-l-2 border-stone-300 pl-3">
            <div className="text-xs uppercase tracking-widest text-stone-500">Category</div>
            <div className={`font-display text-xl mt-1 ${ratingColor(ss.category)}`}>{ss.category || '—'}</div>
          </div>
        </div>
        {ss.headline && <p className="mt-4 text-sm text-stone-700 italic">{ss.headline}</p>}
      </Section>

      {/* Score Reasons */}
      {Array.isArray(insights.scoreReasons) && insights.scoreReasons.length > 0 && (
        <Section title="Why the score is low" icon={TrendingDown}>
          <div className="space-y-3">
            {insights.scoreReasons.map((r, i) => (
              <div key={i} className="flex items-start justify-between gap-4 p-3 bg-stone-100/50 rounded-sm">
                <div className="flex-1">
                  <div className="font-medium text-stone-900">{r.factor}</div>
                  <div className="text-sm text-stone-700 mt-1">{r.detail}</div>
                </div>
                {impactPill(r.impact)}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Utilization */}
      {(() => {
        const hasUtil = ua && (ua.totalLimit || ua.totalBalance || ua.utilizationPct != null);
        const hasPh = ph && (ph.onTimePct != null || ph.latePayments30 != null || ph.latePayments60 != null || ph.latePayments90Plus != null || ph.rating);
        if (!hasUtil && !hasPh) return null;
        return (
          <div className={`grid ${hasUtil && hasPh ? 'md:grid-cols-2' : 'grid-cols-1'} gap-6`}>
            {hasUtil && (
              <Section title="Utilization Analysis" icon={CreditCard}>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-stone-600">Total Limit</span><span className="font-mono text-stone-900">${(ua.totalLimit || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-stone-600">Total Balance</span><span className="font-mono text-stone-900">${(ua.totalBalance || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-stone-600">Utilization</span><span className={`font-display text-2xl ${ratingColor(ua.rating)}`}>{ua.utilizationPct ?? '—'}%</span></div>
                  <div className="pt-2 border-t border-stone-200">
                    <div className={`text-xs uppercase tracking-widest mb-1 ${ratingColor(ua.rating)}`}>{ua.rating}</div>
                    <div className="text-sm text-stone-700">{ua.recommendation}</div>
                  </div>
                </div>
              </Section>
            )}

            {hasPh && (
              <Section title="Payment History" icon={Calendar}>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-stone-600">On-time payments</span><span className={`font-display text-2xl ${ratingColor(ph.rating)}`}>{ph.onTimePct ?? '—'}%</span></div>
                  <div className="flex justify-between"><span className="text-stone-600">30-day late</span><span className="font-mono text-stone-900">{ph.latePayments30 ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-stone-600">60-day late</span><span className="font-mono text-stone-900">{ph.latePayments60 ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-stone-600">90+ day late</span><span className="font-mono text-stone-900">{ph.latePayments90Plus ?? 0}</span></div>
                  <div className="pt-2 border-t border-stone-200">
                    <div className={`text-xs uppercase tracking-widest mb-1 ${ratingColor(ph.rating)}`}>{ph.rating}</div>
                    <div className="text-sm text-stone-700">{ph.recommendation}</div>
                  </div>
                </div>
              </Section>
            )}
          </div>
        );
      })()}

      {/* Improvement Plan */}
      {Array.isArray(insights.improvementPlan) && insights.improvementPlan.length > 0 && (
        <Section title="Personalized Improvement Plan" icon={TrendingUp} accent>
          <div className="space-y-3">
            {insights.improvementPlan.map((s, i) => (
              <div key={i} className="flex items-start gap-4 p-4 border border-stone-200 rounded-sm">
                <div className="w-8 h-8 bg-[#4b5320] text-white flex items-center justify-center rounded-sm font-display font-semibold flex-shrink-0">{s.priority}</div>
                <div className="flex-1">
                  <div className="font-medium text-stone-900">{s.action}</div>
                  <div className="text-xs text-stone-500 mt-1">Timeline: {s.timeline}</div>
                </div>
                <div className="font-mono text-sm text-[#4b5320] font-semibold whitespace-nowrap">{s.estimatedGain}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Balance Paydown Impact */}
      {Array.isArray(insights.balancePaydownImpact) && insights.balancePaydownImpact.length > 0 && (
        <Section title="Balance Paydown Impact" icon={DollarSign}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200 text-xs uppercase tracking-widest text-stone-500">
                  <th className="text-left py-2 font-medium">Account</th>
                  <th className="text-right py-2 font-medium">Current</th>
                  <th className="text-right py-2 font-medium">Pay down to</th>
                  <th className="text-right py-2 font-medium">Est. gain</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200/80">
                {insights.balancePaydownImpact.map((b, i) => (
                  <tr key={i}>
                    <td className="py-2 text-stone-800">{b.account}</td>
                    <td className="py-2 text-right font-mono text-stone-800">${(b.currentBalance || 0).toLocaleString()}</td>
                    <td className="py-2 text-right font-mono text-stone-800">${(b.payDownTo || 0).toLocaleString()}</td>
                    <td className="py-2 text-right font-mono text-[#4b5320] font-semibold">{b.estimatedGain}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Funding Readiness */}
      <Section title="Funding Readiness" icon={CheckCircle2} accent>
        <div className="flex items-baseline gap-3 mb-4">
          <span className={`font-display text-3xl ${ratingColor(fr.currentReadiness)}`}>{fr.currentReadiness || '—'}</span>
          {fr.monthsToReady != null && (
            <span className="text-sm text-stone-600">~{fr.monthsToReady} months to full readiness</span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[
            { key: 'readyForPersonalLoan', label: 'Personal Loan' },
            { key: 'readyForAutoLoan', label: 'Auto Loan' },
            { key: 'readyForMortgage', label: 'Mortgage' },
            { key: 'readyForBusinessCredit', label: 'Business Credit' },
          ].map(({ key, label }) => {
            const ready = fr[key];
            return (
              <div key={key} className={`p-3 border rounded-sm flex items-center gap-2 ${ready ? 'border-[#4b5320]/40 bg-[#4b5320]/5' : 'border-stone-200 bg-stone-50'}`}>
                {ready ? <CheckCircle2 className="w-4 h-4 text-[#4b5320]" /> : <AlertTriangle className="w-4 h-4 text-stone-400" />}
                <div className="text-sm text-stone-800">{label}</div>
              </div>
            );
          })}
        </div>
        {fr.summary && <p className="text-sm text-stone-700">{fr.summary}</p>}
      </Section>

      {insights.generatedAt && (
        <div className="text-xs text-stone-500 text-right">Generated {new Date(insights.generatedAt).toLocaleString()}</div>
      )}

      {/* Email to Client Dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="bg-white border-stone-200 text-stone-800 rounded-sm max-w-lg" data-testid="email-client-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Email Credit Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-700">Recipient</Label>
              <div className="mt-2 px-3 py-2 bg-stone-100 border border-stone-200 rounded-sm text-sm text-stone-800 font-mono" data-testid="email-recipient">
                {clientName ? `${clientName} <${clientEmail}>` : clientEmail}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-700">Personal note (optional)</Label>
              <Textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Hi — I've reviewed your reports. Here's your personalized credit health summary..."
                rows={4}
                className="mt-2 bg-[#fafaf6] border-stone-200 rounded-sm"
                data-testid="email-message-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="attach-pdf"
                checked={attachPdf}
                onCheckedChange={(v) => setAttachPdf(v === true)}
                data-testid="email-attach-pdf"
              />
              <Label htmlFor="attach-pdf" className="text-sm text-stone-700 cursor-pointer">Attach the full PDF report (recommended)</Label>
            </div>
            <div className="text-xs text-stone-500">
              A secure 30-day view link is always included. Client will be able to view the report online or download the PDF.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} className="border-stone-300 bg-white text-stone-700 hover:bg-stone-100 rounded-sm" data-testid="email-cancel-btn">Cancel</Button>
            <Button onClick={sendEmail} disabled={sending} className="bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm" data-testid="email-send-btn">
              {sending ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Mail className="w-4 h-4 mr-2" /> Send Email</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InsightsPanel;
